import { pipe, map, sort, unique, filter } from 'remeda';
import type { Device } from '@types';
import type { HeartRateEvent } from '@types';

type ParsedConfig = {
  videoId?: number;
  currentState?: string;
  clientId?: string;
  deviceModel?: string;
};

export type ConfigurationRecord = {
  id: number | string;
  config?: string | unknown;
  type: string;
  status: string;
  deviceCode?: string;
  deviceName?: string;
  clientId?: string;
  deviceModel?: string;
  videoId?: number | null;
  created_at?: string;
  updated_at?: string;
  patientCode?: string;
  scenario?: string;
  [key: string]: unknown;
};

export type EnrichedRecord = ConfigurationRecord & {
  videoName: string;
  currentState?: string;
  clientId: string;
  deviceModel: string;
  deviceName: string;
  event: HeartRateEvent | null;
};

type LookupMaps = {
  deviceLookup: Map<string, Device>;
  eventsByDeviceCode: Map<string, HeartRateEvent>;
  videoLookup: Map<number, string>;
};

export const parseConfiguration = (
  config: string | unknown,
  type: string
): ParsedConfig => {
  try {
    const configString =
      typeof config === 'string' ? config : JSON.stringify(config);
    const parsedConfig = JSON.parse(configString);
    const result: ParsedConfig = {};

    if (type === 'performance') {
      result.currentState = parsedConfig?.currentState;
    } else {
      result.videoId = Number(parsedConfig?.[0]?.video);
    }

    result.clientId =
      parsedConfig?.clientId || parsedConfig?.[0]?.clientId || undefined;
    result.deviceModel =
      parsedConfig?.deviceModel || parsedConfig?.[0]?.deviceModel || undefined;

    return result;
  } catch {
    return {};
  }
};

export const findDeviceFromLookup = (
  deviceLookup: Map<string, Device>,
  clientId?: string,
  deviceCode?: string,
  deviceName?: string
): Device | null => {
  if (clientId) {
    const device = deviceLookup.get(clientId);
    if (device) return device;
  }

  if (deviceCode) {
    const device = deviceLookup.get(deviceCode);
    if (device) return device;
  }

  if (deviceName) {
    const device = deviceLookup.get(deviceName);
    if (device) return device;
  }

  return null;
};

export const enrichSessionRecord = (
  record: ConfigurationRecord,
  lookups: LookupMaps
): EnrichedRecord => {
  const { deviceLookup, eventsByDeviceCode, videoLookup } = lookups;

  const event =
    record.status === 'in-progress' && record.deviceCode
      ? (eventsByDeviceCode.get(record.deviceCode) ?? null)
      : null;

  const parsedConfig = parseConfiguration(record.config, record.type);
  const resolvedVideoId =
    parsedConfig.videoId ??
    (record.videoId != null ? Number(record.videoId) : undefined);
  const videoName =
    (resolvedVideoId !== undefined && !Number.isNaN(resolvedVideoId)
      ? videoLookup.get(resolvedVideoId)
      : undefined) ?? 'Video non trovato';

  const deviceFromStore = findDeviceFromLookup(
    deviceLookup,
    parsedConfig.clientId,
    record.deviceCode,
    record.deviceName
  );

  const finalClientId =
    parsedConfig.clientId ||
    record.clientId ||
    deviceFromStore?.clientId ||
    (deviceFromStore?.code ? String(deviceFromStore.code) : undefined) ||
    'N/A';
  const finalDeviceModel =
    parsedConfig.deviceModel ||
    record.deviceModel ||
    deviceFromStore?.model ||
    'N/A';
  const finalDeviceName = record.deviceName || deviceFromStore?.name || 'N/A';

  return {
    ...record,
    videoName,
    currentState: parsedConfig.currentState,
    clientId: finalClientId,
    deviceModel: finalDeviceModel,
    deviceName: finalDeviceName,
    event,
  };
};

export const createDeviceLookup = (devices: Device[]): Map<string, Device> => {
  const lookup = new Map<string, Device>();

  devices.forEach(device => {
    if (device.clientId) {
      lookup.set(device.clientId, device);
    }
    if (device.code) {
      lookup.set(device.code, device);
    }
    if (device.name) {
      lookup.set(device.name, device);
    }
  });

  return lookup;
};

export const createEventsByDeviceCodeLookup = (
  events: HeartRateEvent[]
): Map<string, HeartRateEvent> => {
  if (!events || events.length === 0) return new Map();

  const sortedEvents = pipe(
    events,
    sort((a, b) => b.timestamp - a.timestamp)
  );

  const lookup = new Map<string, HeartRateEvent>();

  sortedEvents.forEach(event => {
    if (event.deviceCode && !lookup.has(event.deviceCode)) {
      lookup.set(event.deviceCode, event);
    }
  });

  return lookup;
};

export const createVideoLookup = (
  videosData: Array<{ id: number | string; name: string }>
): Map<number, string> => {
  const lookup = new Map<number, string>();

  if (Array.isArray(videosData)) {
    videosData.forEach(video => {
      if (video.id && video.name) {
        lookup.set(Number(video.id), video.name);
      }
    });
  }

  return lookup;
};

export const createLookupMaps = (
  devices: Device[],
  events: HeartRateEvent[],
  videosData: Array<{ id: number | string; name: string }>
): LookupMaps => {
  return {
    deviceLookup: createDeviceLookup(devices),
    eventsByDeviceCode: createEventsByDeviceCodeLookup(events),
    videoLookup: createVideoLookup(videosData),
  };
};

export const processSessionData = (
  configData: ConfigurationRecord[],
  lookups: LookupMaps
): EnrichedRecord[] => {
  if (!configData || configData.length === 0) return [];

  return pipe(
    configData,
    map(record => enrichSessionRecord(record, lookups))
  );
};

export const extractUniqueTypes = (records: EnrichedRecord[]): string[] => {
  return pipe(
    records,
    map(record => record.type),
    filter(Boolean),
    unique()
  );
};
